import { BMInputField, BMPrimitives } from "@traveloka/district-bloom";
import IcSystemCalendar16 from "@traveloka/icon-kit-web/react/IcSystemCalendar16";
import { useEffect } from "react";
import { Controller, FormProvider } from "react-hook-form";
import { StyleSheet } from "react-native";

import BMView from "../../../components/View/BMView";
import { useMobileContentResource } from "../../../core/resource/MobileResourceProvider";

import { BMDrawer } from "../common";
import { FormGroup } from "../common/FormGroup";
import DivisionsField from "./DivisionsField";
import { useAddUpdateEmployeeForm } from "./hooks";
import { AddUpdateEmployeeFormValues, FormOptions } from "./types";

import { forwardRef, useImperativeHandle } from "react";
import { BMDateInputField } from "../../../components/BMDateInputField";
import { BMDropdownField } from "../../../components/BMDropdownField";
import { BMNationalityField } from "../../../components/BMNationalityField";
import { BMPhoneNumberField } from "../../../components/BMPhoneNumberField";

const ZINDEX_COMMON = 13;

type Props = {
  show: boolean;
  options: FormOptions;
  isLoading: boolean;
  disabled: boolean;
  defaultValue: AddUpdateEmployeeFormValues;
  onClose: () => void;
  onSubmit: (data: AddUpdateEmployeeFormValues) => Promise<void>;
  onClickAddDivision?: (division: string) => void;
};

export type AddUpdateEmployeeFormRef = {
  submit: () => void;
  reset: () => void;
};

const AddUpdateEmployeeForm = forwardRef<AddUpdateEmployeeFormRef, Props>(
  (
    {
      show,
      options,
      isLoading,
      disabled,
      defaultValue,
      onClose,
      onSubmit,
      onClickAddDivision = () => {},
    },
    ref
  ) => {
    const { data, loading, error, refetch } = options;

    const { CorporateManagementEmployeeList: cr } = useMobileContentResource();

    const methods = useAddUpdateEmployeeForm();
    const { control, setValue, handleSubmit, reset } = methods;

    function onSubmitHandler() {
      handleSubmit(async (data) => {
        await onSubmit(data);
      })();
    }

    useImperativeHandle(ref, () => ({
      submit: onSubmitHandler,
      reset: () => reset(defaultValue),
    }));

    useEffect(() => {
      const fields = [
        { key: "role", loading: loading.role, error: error.role },
        {
          key: "division",
          loading: loading.division,
          error: error.division,
        },
        { key: "policy", loading: loading.policy, error: error.policy },
      ] as const;

      fields.forEach(({ key, loading, error }) => {
        if (!loading && !error && defaultValue[key]) {
          setValue(key, defaultValue[key]);
        }
      });
    }, [loading, error, defaultValue, setValue]);

    return (
      <BMView flex justify="center" align="center">
        <BMDrawer
          show={show}
          variant="COMPACT"
          headerNavigationProps={{
            title: cr.addEmployee,
            onBackActionPress: onClose,
          }}
          footerButtonProps={{
            label: cr.saveText,
            onPress: onSubmitHandler,
            loading: isLoading,
            disabled: disabled || isLoading,
          }}
        >
          <FormProvider {...methods}>
            <BMView style={styles.marginVerticalXL}>
              <FormGroup
                label={cr.employeeProfileTitle}
                style={{ zIndex: ZINDEX_COMMON }}
              >
                <Controller
                  control={control}
                  name="fullname"
                  render={({
                    field: { onChange, onBlur, value },
                    formState: { errors },
                  }) => (
                    <BMInputField
                      value={value}
                      label={`${cr.fullnameLabel} *`}
                      placeholder={cr.fullnameInputPlaceholder}
                      error={errors.fullname?.message}
                      onBlur={onBlur}
                      onChangeText={onChange}
                      returnKeyType="done"
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="email"
                  render={({
                    field: { onChange, onBlur, value },
                    formState: { errors },
                  }) => (
                    <BMInputField
                      value={value}
                      label={`${cr.emailLabel} *`}
                      placeholder={cr.emailInputPlaceholder}
                      error={errors.email?.message}
                      onBlur={onBlur}
                      onChangeText={onChange}
                      returnKeyType="done"
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="phone"
                  render={({
                    field: { onChange, onBlur, value },
                    formState: { errors },
                  }) => (
                    <BMPhoneNumberField
                      value={value}
                      prefixOptions={{
                        label: cr.countryCodeLabel,
                        placeholder: cr.countryCodePlaceholder,
                      }}
                      phoneOptions={{
                        label: cr.phoneLabel,
                        placeholder: cr.phoneInputPlaceholder,
                      }}
                      variant="WITH_DASHED"
                      onChange={onChange}
                      onBlur={onBlur}
                      error={
                        errors.phone?.message ||
                        errors.phone?.prefix?.message ||
                        errors.phone?.phone?.message
                      }
                      returnKeyType="done"
                    />
                  )}
                />
              </FormGroup>
              <FormGroup
                label={cr.permissionGroupsTitle}
                style={{ zIndex: ZINDEX_COMMON - 1 }}
              >
                <Controller
                  control={control}
                  name="role"
                  render={({
                    field: { onChange, onBlur, value },
                    formState: { errors },
                  }) => (
                    <BMDropdownField
                      label={cr.accessRoleColumn}
                      value={value}
                      option={{
                        items: data.role || [],
                        isLoading: loading.role,
                        isError: !!error.role,
                        onClickRetry: refetch.role,
                      }}
                      caption={cr.accessRoleInputHelper}
                      isRequired
                      variant="RADIO"
                      optionsSection={{
                        title: cr.accessRoleColumn,
                        description: cr.addAccessRoleDesc,
                      }}
                      onChange={onChange}
                      onBlur={onBlur}
                      error={errors.role?.message}
                      zIndex={ZINDEX_COMMON}
                      returnKeyType="done"
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="policy"
                  render={({
                    field: { onChange, onBlur, value },
                    formState: { errors },
                  }) => (
                    <BMDropdownField
                      label={cr.travelPolicyColumn}
                      value={value}
                      option={{
                        items: data.policy || [],
                        isLoading: loading.policy,
                        isError: !!error.policy,
                        onClickRetry: refetch.policy,
                      }}
                      caption={cr.travelPolicyInputHelper}
                      isRequired
                      variant="RADIO"
                      optionsSection={{
                        title: cr.travelPolicyColumn,
                        description: cr.addTravelPolicyDesc,
                      }}
                      onChange={onChange}
                      onBlur={onBlur}
                      error={errors.policy?.message}
                      zIndex={ZINDEX_COMMON - 1}
                      returnKeyType="done"
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="division"
                  render={({
                    field: { onChange, onBlur, value },
                    formState: { errors },
                  }) => (
                    <DivisionsField
                      value={value}
                      error={errors.division?.message}
                      options={{
                        data: data.division,
                        loading: loading.division,
                        error: error.division,
                        refetch: refetch.division,
                      }}
                      onBlur={onBlur}
                      onChange={onChange}
                      onClickAdd={onClickAddDivision}
                    />
                  )}
                />
              </FormGroup>
              <FormGroup label={cr.travelerDetailsTitle} accordion>
                <Controller
                  control={control}
                  name="birthdate"
                  render={({
                    field: { onChange, onBlur, value },
                    formState: { errors },
                  }) => (
                    <BMDateInputField
                      value={value}
                      label={cr.birthDateInputLabel}
                      error={errors.birthdate?.message}
                      onBlur={onBlur}
                      onChange={onChange}
                      LeadingIcon={IcSystemCalendar16}
                      returnKeyType="done"
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="nationality"
                  render={({
                    field: { onChange, onBlur, value },
                    formState: { errors },
                  }) => (
                    <BMNationalityField
                      value={value}
                      label={cr.nationalityInputLabel}
                      placeholder={cr.nationalityInputPlaceholder}
                      optionsSection={{
                        title: cr.nationalityInputLabel,
                      }}
                      error={errors.nationality?.message}
                      onChange={onChange}
                      onBlur={onBlur}
                      zIndex={ZINDEX_COMMON}
                      returnKeyType="done"
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="identityCardNumber"
                  render={({
                    field: { onChange, onBlur, value },
                    formState: { errors },
                  }) => (
                    <BMInputField
                      value={value ?? ""}
                      label={cr.identityNumberInputLabel}
                      placeholder={cr.identityNumberInputPlaceholder}
                      onChangeText={onChange}
                      onBlur={onBlur}
                      error={errors.identityCardNumber?.message}
                      returnKeyType="done"
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="passportNumber"
                  render={({
                    field: { onChange, onBlur, value },
                    formState: { errors },
                  }) => (
                    <BMInputField
                      value={value ?? ""}
                      label={cr.passportNumberInputLabel}
                      placeholder={cr.passportNumberInputPlaceholder}
                      onChangeText={onChange}
                      onBlur={onBlur}
                      error={errors.passportNumber?.message}
                      returnKeyType="done"
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="passportExpirationDate"
                  render={({
                    field: { onChange, onBlur, value },
                    formState: { errors },
                  }) => (
                    <BMDateInputField
                      value={value}
                      label={cr.passportExpirationDateInputLabel}
                      error={errors.passportExpirationDate?.message}
                      onChange={onChange}
                      onBlur={onBlur}
                      LeadingIcon={IcSystemCalendar16}
                      returnKeyType="done"
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="passportCountryOfIssuance"
                  render={({
                    field: { onChange, onBlur, value },
                    formState: { errors },
                  }) => (
                    <BMNationalityField
                      value={value}
                      label={cr.countryIssuanceInputLabel}
                      placeholder={cr.countryIssuanceInputPlaceholder}
                      optionsSection={{
                        title: cr.countryIssuanceInputLabel,
                      }}
                      error={errors.passportCountryOfIssuance?.message}
                      onChange={onChange}
                      onBlur={onBlur}
                      returnKeyType="done"
                    />
                  )}
                />
              </FormGroup>
            </BMView>
          </FormProvider>
        </BMDrawer>
      </BMView>
    );
  }
);

export default AddUpdateEmployeeForm;

const styles = StyleSheet.create({
  marginVerticalXL: {
    marginVertical: BMPrimitives.BMSize.xl,
  },
});
